import { Controller, useForm } from "react-hook-form";
import { StyledDialog } from "../../../components/StyledComponents/Dialog/Dialog.js";
import { Box, DialogContent } from "@mui/material";
import {
  BodyMedium,
  H6HeadlineSmall,
} from "../../../components/StyledComponents/Typography/Typography.tsx";
import {
  PrimaryButton,
  SecondaryButton,
  StyledIconButton,
} from "../../../components/StyledComponents/Buttons/AuradineButtons.js";
import React, { useEffect, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import { useAuth0 } from "@auth0/auth0-react";
import DataGrid from "../../../components/DataGrid/DataGrid.tsx";
import { usePostOrg } from "../../../api/queries/usePostOrg.ts";
import AuradineErrorState from "../../../components/StyledComponents/EmptyStates/AuradineErrorState.tsx";
import { has, isEmpty } from "lodash";
import { StyledCheckbox } from "../../../components/StyledComponents/Inputs/Inputs.js";
import { useAcceptMinerTransfer } from "../../queries/useAcceptMinerTransfer.ts";
import { useRejectMinerTransfer } from "../../queries/useRejectMinerTransfer.ts";
import useAppContextProvider from "../../../AppContext/useAppContextProvider.js";
import { useGetSiteLists } from "../../../api/queries/useGetSiteLists.ts";
import {
  QueryClient,
  useQueryClient,
} from "@tanstack/react-query";
import { useTheme } from "../../../ThemeContext/ThemeContext.js";
import { StyledDataGrid } from "../../../components/StyledComponents/DataGrid/StyledDataGrid.tsx";

const JobsHeader = ({ setOpen }) => {
  const { colors } = useTheme();
  return (
    <Box
      display={"flex"}
      flexDirection={"row"}
      justifyContent={"space-between"}
      flexGrow={1}
      padding={"1.5rem 0 0 0"}
    >
      <Box
        display={"flex"}
        flexDirection={"column"}
        gap={2}
      >
        <H6HeadlineSmall>
          {"Accept/Reject transfers"}
        </H6HeadlineSmall>

        <BodyMedium>
          Use this window to view and manage miner transfer jobs between organizations.
        </BodyMedium>
      </Box>

      <StyledIconButton   
        colors={colors}
        onClick={() => {
          setOpen(false);
        }}
      >
        <CloseIcon />
      </StyledIconButton>
    </Box>
  );
};

const TransferJobsForm = ({ open, setOpen, refetch }) => {
  const { colors } = useTheme();
  const [authToken, setAuthToken] = useState("");
  const [selectionModel, setSelectionModel] = useState([]);
  const { setToastMessage, setToastOpen } =
    useAppContextProvider();
  const handleTableSelectionChange = (newSelection) => {
    setSelectionModel(newSelection);
  };
  const queryClient = useQueryClient();
  console.log("queryClient", queryClient);

  const { refetch: siteListsRefetch } = useGetSiteLists({
    authToken: authToken,
  });
  const { getAccessTokenSilently } = useAuth0();

  const projection = { transfer: 1, name: 1 };
  const {
    data: transferJobsData,
    refetch: transferJobsDataRefetch,
    isLoading: isTransferJobsDataLoading,
    isFetching: isTransferJobsDataFetching,
    isFetched: isTransferDataFetched,
    isError: isTransferDataError,
    error: TransferDataError,
    isRefetching: isTransferDataRefetching,
  } = usePostOrg({
    open: open,
    authToken,
    ...{
      projection: projection,
    },
  });

  const orgName =
    transferJobsData && transferJobsData.result
      ? transferJobsData.result.name
      : "";
  console.log("transferJobsData", orgName);
  let operationLogsModified = Array.isArray(
    transferJobsData?.result?.transfer
  )
    ? transferJobsData.result.transfer
        .map((item) => ({
          ...item,
        }))
        .filter((itemToBeFiltered) => {
          return (
            itemToBeFiltered.source_org_name !== orgName
          );
        })
    : [];

  const CustomGridRowsOverlay = () => {
    console.log(
      "transferJobsData in custom grid rows",
      transferJobsData
    );
    if (
      transferJobsData &&
      transferJobsData.status === "ok" &&
      operationLogsModified.length === 0
    ) {
      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="100%"
          sx={{
            backgroundColor: colors.background.popover, // Custom background color
            color: colors.text.muted, // Custom text color
            fontSize: "1.2rem",
            fontWeight: "bold",
            overflow: "auto",
          }}
        >
          No data available
        </Box>
      );
    }
    if (
      isTransferDataError ||
      (isTransferDataFetched &&
        transferJobsData.status.toLowerCase() === "error")
    ) {
      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="100%"
          sx={{
            backgroundColor: colors?.background?.popover, // Custom background color
            color: colors?.text?.muted, // Custom text color
            fontSize: "1.2rem",
            fontWeight: "bold",
            overflow: "auto",
          }}
        >
          <AuradineErrorState message="Oops!! Something went wrong!!" />
        </Box>
      );
    }
  };

  const closeDialog = async (data) => {
    setOpen(false);
  };

  const { mutateAsync: acceptTransfer } =
    useAcceptMinerTransfer();
  const { mutateAsync: rejectTransfer } =
    useRejectMinerTransfer();

  const handleTransferRejection = async () => {
    const promises = await Promise.allSettled(
      selectionModel.map((item) => {
        return rejectTransfer({
          jobid: item,
          token: authToken,
        });
      })
    );
    const isFullfilled = promises.every(
      (promise) =>
        promise.status === "fulfilled" &&
        promise.value.status !== "error"
    );
    console.log("promises", promises);
    if (isFullfilled) {
      setToastMessage(
        `Transfer with IDs ${selectionModel.join(
          ","
        )} rejected successfully`
      );
      setToastOpen(true);
      await refetch();
      await transferJobsDataRefetch();
      await siteListsRefetch();
      queryClient.invalidateQueries({
        queryKey: ["getSiteLists"],
      });
    } else {
      const rejectedPromises = promises.filter(
        (promise) =>
          promise.status === "fulfilled" &&
          promise.value.status === "error"
      );
      const errorMessages = rejectedPromises.map(
        (promise) => promise.value.msg
      );
      setToastMessage(
        `Transfer with IDs ${selectionModel.join(
          ","
        )} failed: ${errorMessages.join(", ")}`
      );
      setToastOpen(true);
    }
  };

  const handleTransferAcceptance = async () => {
    const promises = await Promise.allSettled(
      selectionModel.map((item) => {
        return acceptTransfer({
          jobid: item,
          token: authToken,
        });
      })
    );

    const isFullfilled = promises.every(
      (promise) =>
        promise.status === "fulfilled" &&
        promise.value.status !== "error"
    );
    if (isFullfilled) {
      setToastMessage(
        `Transfer with IDs ${selectionModel.join(
          ","
        )} accepted successfully`
      );
      setToastOpen(true);
      await refetch();
      await transferJobsDataRefetch();
      await siteListsRefetch();
    } else {
      const rejectedPromises = promises.filter(
        (promise) =>
          promise.status === "fulfilled" &&
          promise.value.status === "error"
      );
      const errorMessages = rejectedPromises.map(
        (promise) => promise.value.msg
      );

      setToastMessage(
        `Transfer with IDs ${selectionModel.join(
          ","
        )} failed: ${errorMessages.join(", ")}`
      );
      setToastOpen(true);
      await refetch();
      await transferJobsDataRefetch();
    }
  };

  const columns = [
    {
      field: "jobid",
      headerName: "ID",
      flex: 1,
      minWidth: 100,
    },
    {
      field: "source_org_name",
      headerName: "Source Org",
      flex: 1,
      minWidth: 100,
    },
    {
      field: "target_org_id",
      headerName: "Target Org ID",
      flex: 1,
      minWidth: 100,
    },
    {
      field: "source_email",
      headerName: "Source Email",
      flex: 1,
      minWidth: 100,
    },
    {
      field: "target_email",
      headerName: "Target Email",
      flex: 1,
      minWidth: 100,
    },
  ];
  console.log(
    "operationLogsModified",
    operationLogsModified
  );
  const dataGridProps = {
    rows: operationLogsModified || [],
    columns: columns,
    autoHeight: true,
    hideFooter: true,
    checkboxSelection: true,
    disableSelectionOnClick: true,
    disableCellSelection: true,
    getRowId: (row) => row.jobid,
    loading:
      isTransferJobsDataLoading ||
      isTransferJobsDataFetching,
    slots: {
      baseCheckbox: StyledCheckbox,
      noRowsOverlay: CustomGridRowsOverlay,
    },
    slotProps: {
      loadingOverlay: {
        variant: "linear-progress",
        noRowsVariant: "skeleton",
        sx: {
          backgroundColor: colors.background.popover,
        },
      },
    },
    selectionModel: selectionModel,
    onRowSelectionModelChange: handleTableSelectionChange,
  };

  React.useEffect(() => {
    const fetchToken = async () => {
      try {
        const token = await getAccessTokenSilently();
        setAuthToken(token);
      } catch (error) {
        console.error(" Error fetching token:", error);
      }
    };
    fetchToken();
  }, [getAccessTokenSilently]);

  return (
    <Box display={"flex"} flexDirection={"column"} gap={4}>
      <JobsHeader setOpen={setOpen} />
      <Box
        display={"flex"}
        flexDirection={"row"}
        justifyContent={"flex-end"}
        flexgrow={1}
        gap={2}
      >
        <SecondaryButton colors={colors}
          onClick={handleTransferRejection}
          disabled={selectionModel.length === 0}
        >
          Reject transfer
        </SecondaryButton>
        <PrimaryButton colors={colors}
          onClick={handleTransferAcceptance}
          disabled={selectionModel.length === 0}
        >
          Accept transfer
        </PrimaryButton>
      </Box>
      <Box display={"flex"} flexGrow={1}>
        <>
          {" "}
          <StyledDataGrid
            colors={colors}
            {...dataGridProps }
            height={"50vh"}
            maxHeight="50vh"
            sx={{
              "& .MuiDataGrid-cell": {
                whiteSpace: "normal",
                wordBreak: "break-word",
                display: "flex",
                alignItems: "flex-start",
              },
            }}
          />
        </>
      </Box>
    </Box>
  );
};

const TransferJobsDialog = ({ open, setOpen, refetch }) => {
  const { colors } = useTheme();
  return (
    <StyledDialog
      colors={colors}
      maxWidth={"lg"}
      fullWidth
      open={open}
      hideBackdrop
      onClose={(event, reason) => {
        if (reason === "backdropClick") {
          setOpen(false);
        }
      }}
    >
      <DialogContent sx={{ padding: 0 }}>
        <TransferJobsForm
          open={open}
          setOpen={setOpen}
          refetch={refetch}
        />
      </DialogContent>
    </StyledDialog>
  );
};

export default TransferJobsDialog;
